/**
 * useEntrance — the one way a screen animates its content in.
 *
 * WHY THIS EXISTS
 *
 * STANDARDS §8: "No se anima lo que remonta." An entrance animation on a
 * component that unmounts and remounts produces flicker, not elegance.
 *
 * Every query-backed screen swaps its inner region between skeleton, error,
 * empty and content. Those are the same JSX position holding different
 * children, so React unmounts one subtree and mounts the other. A raw
 * `entering={FadeInDown...}` inside the content therefore replays the whole
 * cascade on *every* transition: refetch after a failed request, a filter
 * change, a mutation that briefly empties the list. The user asked for one
 * thing and the screen re-introduced itself.
 *
 * The fix is to keep the latch outside the subtree that remounts. This hook
 * is called in the screen component — which stays mounted, because
 * `ScreenContainer` is hoisted above the branch — so the "already entered"
 * state survives the remount underneath it.
 *
 * USAGE
 *
 *   const enter = useEntrance(!!vehiclesQuery.data)
 *   ...
 *   <Animated.View entering={enter(1)}>…</Animated.View>
 *   <Animated.View entering={enter(2)}>…</Animated.View>
 *
 * `ready` is "the real content is on screen now". Pass the same condition the
 * screen uses to render content instead of a skeleton; until it flips true the
 * hook stays armed, so the cascade lands on the content rather than being
 * burned during the loading state.
 *
 * The step argument is an ordinal position in the cascade, not milliseconds:
 * step 1 enters one STAGGER unit after step 0. Steps are clamped to
 * MAX_STAGGER_STEPS so a long list never ends with a row that looks stuck.
 *
 * Reduced motion returns `undefined` for every step — content simply appears.
 */

import { useCallback, useEffect, useState } from 'react'
import { FadeInDown, useReducedMotion } from 'react-native-reanimated'
import { isLegacyIosFabricCrashRuntime } from '../platform/legacy-ios-fabric'
import { DURATION, MAX_STAGGER_STEPS, STAGGER } from '../theme/tokens'

/** What an `entering` prop accepts, plus the reduced-motion opt-out. */
export type Entrance = ReturnType<typeof FadeInDown.duration> | undefined

/**
 * The callback `useEntrance` hands back. Pass it down to a child component
 * that renders part of the same cascade — never call `useEntrance` inside a
 * child that remounts, or the latch remounts with it and the bug comes back.
 */
export type EntranceFn = (step?: number) => Entrance

export function useEntrance(ready: boolean = true): EntranceFn {
  const reducedMotion = useReducedMotion()
  const skipLayoutAnimation = isLegacyIosFabricCrashRuntime()

  // Armed until the content has been on screen once. Deliberately state and
  // not a ref written during render: a render-time latch disarms itself on
  // React's double-render pass and the animation never plays at all.
  const [armed, setArmed] = useState(true)

  useEffect(() => {
    // `armed` en la guarda y en las deps a propósito: desarmado no se vuelve a
    // tocar el estado. Sin esto, un padre cuyo `ready` oscila (refetch que
    // alterna datos y skeleton, o una navegación que remonta en cadena) hacía
    // que cada re-run pasara por setArmed y el contador de updates anidados de
    // React llegara a 50 — "Maximum update depth exceeded" con este efecto como
    // frame culpable en el stack (TAGO-MOBILE-56).
    //
    // Y el desarme va DIFERIDO un frame, no sincrónico en el efecto: React
    // ejecuta los efectos pasivos dentro de un contexto de commit, así que un
    // setState ahí ADENTRO suma al mismo contador de updates anidados que una
    // tormenta de remontajes está incrementando — el stack del 56 revienta
    // exactamente en el `dispatchSetState` de esta línea (use-entrance.ts:73).
    // Un timeout corre como tarea nueva, fuera de ese contexto: el update es un
    // commit limpio que no puede encadenarse con la tormenta. El frame de
    // contenido ya recibió su animación (armed sigue true durante ese render);
    // el siguiente render ya ve el latch desarmado.
    if (!ready || !armed) return undefined
    const handle = setTimeout(() => setArmed(false), 0)
    return () => clearTimeout(handle)
  }, [ready, armed])

  return useCallback(
    (step: number = 0): Entrance => {
      if (reducedMotion || skipLayoutAnimation || !armed) return undefined
      const position = Math.min(Math.max(Math.trunc(step), 0), MAX_STAGGER_STEPS)
      return FadeInDown.duration(DURATION.normal).delay(position * STAGGER)
    },
    [reducedMotion, skipLayoutAnimation, armed],
  )
}
